/*
 * Algolia autocomplete popup (Algolia AI Search & Discovery app).
 *
 * Uses the app's hooks API to replace the default panel with a split layout:
 * - the typed query + "see results" pill;
 * - featured models as chips and category links (a Shopify menu);
 * - results: live product previews from the app's products source, with the
 *   total hit count.
 *
 * Content comes from #AddaAutocompleteConfig (snippets/algolia-autocomplete-config),
 * edited in Theme settings > Search.
 */
(function () {
  var MOUNT_SELECTOR = '.header__search-desktop, .adda__header--mobile-search-row';
  var MIN_QUERY_LENGTH = 2;
  var MAX_FEATURED = 6;
  var MAX_OTHER = 6;
  var PRODUCTS_PER_PANEL = 6;

  function readConfig() {
    var el = document.getElementById('AddaAutocompleteConfig');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  var config = readConfig();
  var searchUrl = config.searchUrl || '/search';

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function resultsUrl(query) {
    return searchUrl + '?q=' + encodeURIComponent(query);
  }

  /* Featured models matching the query; all of them when nothing matches. */
  function featuredFor(query) {
    var featured = config.featured || [];
    var words = normalize(query)
      .split(/\s+/)
      .filter(function (word) {
        return word.length >= MIN_QUERY_LENGTH;
      });
    var matches = featured.filter(function (item) {
      var text = normalize(item.brand + ' ' + item.model);
      return words.some(function (word) {
        return text.indexOf(word) !== -1;
      });
    });
    return (matches.length ? matches : featured).slice(0, MAX_FEATURED);
  }

  /* Menu links, skipping the one the shopper is already searching for. */
  function otherFor(query) {
    var q = normalize(query).trim();
    return (config.other || [])
      .filter(function (link) {
        return !q || normalize(link.title) !== q;
      })
      .slice(0, MAX_OTHER);
  }

  /* ---------- Templates ---------- */

  function arrowIcon(html) {
    return html`
      <svg
        class="adda-ac-icon"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          d="M5 12h14M13 6l6 6-6 6"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        ></path>
      </svg>
    `;
  }

  function introTemplate(query, html) {
    return html`
      <a href="${resultsUrl(query)}" class="adda-ac-intro">
        <span class="adda-ac-eyebrow">Você buscou</span>
        <span class="adda-ac-intro__query">${query}</span>
        <span class="adda-ac-intro__cta">Ver resultados ${arrowIcon(html)}</span>
      </a>
    `;
  }

  function suggestionsTemplate(item, html) {
    var chips = item.featured.map(function (model) {
      var text = model.brand + (model.model ? ' ' + model.model : '');
      return html`
        <li>
          <a href="${resultsUrl(text)}" class="adda-ac-chip">
            <strong>${model.brand}</strong>${model.model ? ' ' + model.model : ''}
          </a>
        </li>
      `;
    });
    var links = item.other.map(function (link) {
      return html`
        <li>
          <a href="${link.url}" class="adda-ac-category">
            <span>${link.title}</span>${arrowIcon(html)}
          </a>
        </li>
      `;
    });

    return html`
      <div class="adda-ac-suggestions">
        ${
          chips.length
            ? html`
                <div class="adda-ac-group">
                  <p class="adda-ac-eyebrow">${config.featuredTitle}</p>
                  <ul class="adda-ac-chips" role="list">
                    ${chips}
                  </ul>
                </div>
              `
            : null
        }
        ${
          links.length
            ? html`
                <div class="adda-ac-group">
                  <p class="adda-ac-eyebrow">${config.otherTitle}</p>
                  <ul class="adda-ac-categories" role="list">
                    ${links}
                  </ul>
                </div>
              `
            : null
        }
      </div>
    `;
  }

  /* ---------- Plugin ---------- */

  var explorePlugin = {
    name: 'adda-explore',
    getSources: function (params) {
      var query = (params.query || '').trim();
      return [
        {
          sourceId: 'addaExplore',
          getItems: function () {
            var featured = featuredFor(query);
            var other = otherFor(query);
            return featured.length || other.length ? [{ featured: featured, other: other }] : [];
          },
          templates: {
            item: function (args) {
              return suggestionsTemplate(args.item, args.html);
            },
          },
        },
      ];
    },
  };

  /* ---------- Hooks ---------- */

  function autocompleteOptions(options) {
    options.placeholder = 'Busque por peças e marcas';
    options.openOnFocus = true;
    options.translations = {
      clearButtonTitle: 'Limpar',
      detachedCancelButtonText: 'Cancelar',
      submitButtonTitle: 'Buscar',
    };
    options.onSubmit = function (params) {
      if (params && params.state && params.state.query) {
        window.location.href = resultsUrl(params.state.query);
      }
    };
    options.plugins = [explorePlugin].concat(options.plugins || []);
    // Drop sources passed directly as options (plugins provide ours).
    options.getSources = function () {
      return [];
    };
    return options;
  }

  /* Keep only the app's products source and its redirect rules. */
  function pluginsArray() {
    var algolia = window.algoliaShopify || {};
    return Array.prototype.slice.call(arguments).filter(function (plugin) {
      return (
        plugin && (plugin === algolia.productsPlugin || plugin.name === 'aa.redirectUrlPlugin')
      );
    });
  }

  function pluginOptions(options, meta) {
    if (meta && meta.pluginName === 'products' && options.searchParameters) {
      options.searchParameters.hitsPerPage = PRODUCTS_PER_PANEL;
    }
    return options;
  }

  function productTemplate(_default, templateOptions, distinct, itemLink, handleItemClick) {
    var html = templateOptions.html;
    var item = templateOptions.item;
    var helpers = window.algoliaShopify.helpers;
    var image = helpers.renderImage(item, 300);
    var discount =
      item.compare_at_price > item.price
        ? Math.round((1 - item.price / item.compare_at_price) * 100)
        : 0;

    return html`
      <a
        href="${itemLink}"
        class="adda-ac-product"
        onClick="${function () {
          handleItemClick(item);
        }}"
      >
        <span class="adda-ac-product__media">
          ${
            image && image.src
              ? html`<img
                  src="${image.src}"
                  srcset="${image.srcset}"
                  sizes="${image.sizes}"
                  alt="${item.title}"
                  loading="lazy"
                />`
              : null
          }
          ${discount > 0 ? html`<span class="adda-ac-product__badge">-${discount}%</span>` : null}
        </span>
        ${item.vendor ? html`<span class="adda-ac-product__vendor">${item.vendor}</span>` : null}
        <span class="adda-ac-product__title"
          >${templateOptions.components.Highlight({ hit: item, attribute: 'title' })}</span
        >
        <span class="adda-ac-product__price"
          >${helpers.displayPrice(item, distinct, item._productLevel)}</span
        >
      </a>
    `;
  }

  function resultsHeader(html, state) {
    var count = state.context && state.context.nbProducts;
    if (!state.query) return html`<p class="adda-ac-eyebrow">Peças em destaque</p>`;
    return html`
      <div class="adda-ac-results__header">
        <p class="adda-ac-eyebrow">
          ${count ? html`<span class="adda-ac-count">${count}</span> ${count === 1 ? 'peça' : 'peças'}` : 'Peças'}
        </p>
        <a href="${resultsUrl(state.query)}" class="adda-ac-more">Ver todas ${arrowIcon(html)}</a>
      </div>
    `;
  }

  /* Areas: intro + suggestions on the left, results on the right (desktop);
     intro, results, suggestions stacked (mobile). */
  function mainTemplate(_default, templateOptions, elements) {
    var html = templateOptions.html;
    var query = (templateOptions.state.query || '').trim();
    var hasIntro = query.length >= MIN_QUERY_LENGTH;
    return html`
      <div
        class="aa-PanelLayout aa-Panel--scrollable adda-ac${hasIntro ? '' : ' adda-ac--no-intro'}"
      >
        ${hasIntro ? html`<div class="adda-ac__intro">${introTemplate(query, html)}</div>` : null}
        ${
          elements.products
            ? html`
                <div class="adda-ac__results">
                  ${resultsHeader(html, templateOptions.state)} ${elements.products}
                </div>
              `
            : null
        }
        <div class="adda-ac__suggestions">${elements.addaExplore}</div>
        ${elements.redirectUrlPlugin}
      </div>
    `;
  }

  function noResultsTemplate(_default, templateOptions) {
    var html = templateOptions.html;
    var query = (templateOptions.state.query || '').trim();
    return html`
      <div class="aa-PanelLayout adda-ac adda-ac--empty">
        ${query ? html`<div class="adda-ac__intro">${introTemplate(query, html)}</div>` : null}
        <p class="adda-ac-eyebrow">Buscar na loja</p>
        <p class="adda-ac-empty">
          <a
            href="${resultsUrl(query)}"
            class="adda-ac-more"
            style="display:inline-flex;align-items:center;gap:4px;"
          >
            Ver resultados para "${query}" ${arrowIcon(html)}
          </a>
        </p>
      </div>
    `;
  }

  function register() {
    var hooks = window.algoliaShopify && window.algoliaShopify.hooks;
    if (!hooks) return;
    hooks.registerHook('beforeAutocompleteOptions', autocompleteOptions);
    hooks.registerHook('beforeAutocompletePluginsArray', pluginsArray);
    hooks.registerHook('beforeAutocompletePluginOptions', pluginOptions);
    hooks.registerHook('beforeAutocompleteProductTemplate', productTemplate);
    hooks.registerHook('beforeAutocompleteMainTemplate', mainTemplate);
    hooks.registerHook('beforeAutocompleteNoResultsTemplate', noResultsTemplate);
  }

  if (window.algoliaShopify && window.algoliaShopify.hooks) {
    register();
  } else {
    document.addEventListener('algolia.hooks.initialize', register);
  }

  // The app empties the element matched by input_selector and mounts its own
  // search form inside it, so it must be a container, never the <input>.
  // Overrides the selector set in the Algolia admin.
  document.addEventListener('algolia.autocomplete.beforeInit', function () {
    var algolia = window.algoliaShopify;
    if (algolia && algolia.config) algolia.config.input_selector = MOUNT_SELECTOR;
  });
})();
